import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { t, setLanguage, getLanguage, availableLanguages } from '../../i18n';
import {
  LayoutDashboard, Package, BarChart3, Truck, Users, ShoppingCart,
  Globe, Wallet, FileText, Settings, LogOut, Menu, X, ChevronDown,
  AlertTriangle, User, HelpCircle, ScrollText, CreditCard,
  FileSignature, RotateCcw, ClipboardList, Tag, Receipt,
} from 'lucide-react';
import AideModal from './AideModal';
import { resolveUploadUrl, initiales } from '../../utils/media';

// Renvoie blanc ou noir selon la luminance d'une couleur de fond (pour le texte par-dessus)
function textColorOnIcon(hex) {
  if (!hex) return '#ffffff';
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (h.length !== 6) return '#ffffff';
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.55 ? '#0f172a' : '#ffffff';
}

// ─────────────────────────────────────────────────────────────────────────────
// Matrice de visibilité côté Sidebar (les écritures sont en plus filtrées par
// permissions côté backend ET par les boutons cachés dans les pages).
//
//  - admin        : VOIT tout (lecture). Édite uniquement /parametres et /users.
//  - proprietaire : VOIT tout, ÉDITE tout sauf /users.
//  - gerant_vendeur : VOIT et ÉDITE le métier de sa boutique (sauf paramètres,
//                     users, rapports financiers réservés au propriétaire).
//  - vendeur      : N'A AUCUN ACCÈS à l'app (rôle conservé en DB mais sans UI).
// ─────────────────────────────────────────────────────────────────────────────
// `moduleCode` (optionnel) : code d'un module dans modules_disponibles.
// Si présent, l'item n'est affiché que quand le commerce a ce module actif.
// Si absent (ou correspond à un essentiel), toujours affiché.
const navItems = [
  // Ordre voulu par le commerce : flux quotidien d'abord
  { to: '/dashboard',     icon: LayoutDashboard, key: 'nav.dashboard',     roles: ['admin', 'proprietaire', 'gerant_vendeur'] },
  { to: '/caisse',        icon: Wallet,          key: 'nav.caisse',        roles: ['admin', 'proprietaire'],                   moduleCode: 'caisse' },
  { to: '/ventes',        icon: ShoppingCart,    key: 'nav.ventes',        roles: ['admin', 'proprietaire', 'gerant_vendeur'], moduleCode: 'ventes_magasin' },
  { to: '/commandes',     icon: Globe,           key: 'nav.commandes',     roles: ['admin', 'proprietaire', 'gerant_vendeur'], moduleCode: 'commandes_ligne' },
  { to: '/vendeurs',      icon: Users,           key: 'nav.vendeurs',      roles: ['admin', 'proprietaire', 'gerant_vendeur'], moduleCode: 'vendeurs_terrain' },
  { to: '/stock',         icon: BarChart3,       key: 'nav.stock',         roles: ['admin', 'proprietaire', 'gerant_vendeur'], moduleCode: 'stock' },
  { to: '/bons-commande', icon: FileText,        key: 'nav.bonsCommande',  roles: ['admin', 'proprietaire', 'gerant_vendeur'], moduleCode: 'bons_commande' },
  // Le reste (gestion / configuration)
  { to: '/produits',      icon: Package,         key: 'nav.produits',      roles: ['admin', 'proprietaire', 'gerant_vendeur'], moduleCode: 'produits' },
  { to: '/fournisseurs',  icon: Truck,           key: 'nav.fournisseurs',  roles: ['admin', 'proprietaire', 'gerant_vendeur'], moduleCode: 'fournisseurs' },
  { to: '/rapports',      icon: FileText,        key: 'nav.rapports',      roles: ['admin', 'proprietaire'],                   moduleCode: 'rapports' },
  { to: '/journal-ventes', icon: ScrollText,     key: 'nav.journalVentes', roles: ['admin', 'proprietaire', 'gerant_vendeur'] },
  { to: '/devis',             icon: FileSignature,  key: 'nav.devis',             roles: ['admin', 'proprietaire', 'gerant_vendeur'], moduleCode: 'devis' },
  { to: '/retours-clients',   icon: RotateCcw,      key: 'nav.retoursClients',   roles: ['admin', 'proprietaire', 'gerant_vendeur'], moduleCode: 'retours_clients' },
  { to: '/inventaires',       icon: ClipboardList,  key: 'nav.inventaires',      roles: ['admin', 'proprietaire', 'gerant_vendeur'], moduleCode: 'inventaires' },
  { to: '/prix',              icon: Tag,            key: 'nav.prix',             roles: ['admin', 'proprietaire'],                   moduleCode: 'prix_avances' },
  { to: '/taxes',             icon: Receipt,        key: 'nav.taxes',            roles: ['admin', 'proprietaire'],                   moduleCode: 'taxes' },
  { to: '/parametres',    icon: Settings,        key: 'nav.parametres',    roles: ['admin', 'proprietaire'] },
  { to: '/abonnement',    icon: CreditCard,      key: 'nav.abonnement',    roles: ['admin', 'proprietaire'] },
  { to: '/activite',      icon: ScrollText,      key: 'nav.activite',      roles: ['admin', 'proprietaire'] },
];

export default function Sidebar({ alertesStock = 0, mobileOpen, onMobileChange, hideMobileToggle = false }) {
  const {
    user, logout, moduleActif, logoUrl, nomCommerce, loading,
    couleurFond, couleurIcones, couleurTexte,
  } = useAuth();

  // Mettre à jour le titre de l'onglet selon le commerce
  React.useEffect(() => {
    if (nomCommerce) document.title = nomCommerce;
  }, [nomCommerce]);
  const navigate = useNavigate();
  // Mode contrôlé (Layout pilote) ou non-contrôlé (fallback hérité)
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = typeof mobileOpen === 'boolean';
  const open = isControlled ? mobileOpen : internalOpen;
  const setOpen = (v) => {
    if (isControlled) onMobileChange?.(v);
    else setInternalOpen(v);
  };
  const [lang, setLang] = useState(getLanguage());
  const [langOpen, setLangOpen] = useState(false);
  const [showAide, setShowAide] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleLang = (code) => {
    setLanguage(code);
    setLang(code);
    setLangOpen(false);
    window.location.reload();
  };

  // Pages de réglages/administration interdites au profil consultation.
  const PAGES_REGLAGES = new Set(['/parametres', '/abonnement', '/activite']);
  // Vue volontairement restreinte de l'ADMIN du commerce : supervision
  // (tableau de bord), gestion du catalogue (créer produit / fournisseur)
  // et administration (paramètres, abonnement, activité). Le métier quotidien
  // (ventes, caisse, stock, commandes…) appartient au propriétaire / gérant.
  const ADMIN_VISIBLE = new Set([
    '/dashboard', '/produits', '/fournisseurs',
    '/parametres', '/abonnement', '/activite',
  ]);
  // Filtrer par rôle ET par module activé pour le commerce (lot L07).
  // - admin : uniquement les 6 entrées ci-dessus.
  // - consultation : TOUTES les pages métier (lecture seule) sauf réglages.
  // - autres : selon item.roles.
  const visibleItems = navItems
    .filter(item => {
      if (user?.role === 'admin') return ADMIN_VISIBLE.has(item.to);
      if (user?.role === 'consultation') return !PAGES_REGLAGES.has(item.to);
      return item.roles.includes(user?.role);
    })
    .filter(item => moduleActif(item.moduleCode));
  const currentLangLabel = availableLanguages.find(l => l.code === lang);

  // Couleur de texte secondaire = texte principal estompé (60% opacité)
  const couleurTexteSoft = couleurTexte === '#ffffff'
    ? 'rgba(255,255,255,0.7)' : 'rgba(15,23,42,0.6)';
  const couleurHoverBg = couleurTexte === '#ffffff'
    ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';

  const SidebarContent = () => (
    <div
      className="flex flex-col h-full"
      style={{ backgroundColor: couleurFond, color: couleurTexte }}
    >
      {/* Logo + nom dynamiques depuis le commerce */}
      <div
        className="p-4 border-b"
        style={{ borderColor: couleurTexte === '#ffffff'
          ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)' }}
      >
        <button
          onClick={() => { setOpen(false); navigate('/dashboard'); window.location.reload(); }}
          className="flex items-center space-x-3 w-full hover:opacity-80 transition-opacity cursor-pointer"
          title="Retour à l'accueil"
        >
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={nomCommerce}
              className="w-12 h-12 object-contain rounded-lg bg-white p-0.5"
              onError={e => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div
              className="w-12 h-12 rounded-lg flex items-center justify-center font-bold text-lg"
              style={{ backgroundColor: couleurIcones + '30', color: couleurTexte }}
            >
              {nomCommerce ? nomCommerce.substring(0, 2).toUpperCase() : '·'}
            </div>
          )}
          <div className="text-left">
            <h1 className="font-bold text-sm leading-tight" style={{ color: couleurTexte }}>
              {loading && !nomCommerce ? '…' : nomCommerce}
            </h1>
            {user?.role && (
              <p className="text-xs font-medium" style={{ color: couleurTexteSoft }}>
                {user.role === 'admin' ? 'Administrateur'
                  : user.role === 'proprietaire' ? 'Propriétaire'
                  : user.role === 'consultation' ? 'Consultation (lecture seule)'
                  : 'Gérant/Vendeur'}
              </p>
            )}
          </div>
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {visibleItems.map(({ to, icon: Icon, key }) => (
          <NavLink
            key={to}
            to={to}
            onClick={() => setOpen(false)}
            style={({ isActive }) => isActive
              ? { backgroundColor: couleurIcones, color: textColorOnIcon(couleurIcones) }
              : { color: couleurTexte }
            }
            className={({ isActive }) =>
              `flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all duration-200 text-sm
              ${isActive ? 'font-semibold' : 'hover:bg-white/10'}`
            }
            onMouseEnter={e => {
              if (!e.currentTarget.classList.contains('font-semibold')) {
                e.currentTarget.style.backgroundColor = couleurHoverBg;
              }
            }}
            onMouseLeave={e => {
              if (!e.currentTarget.classList.contains('font-semibold')) {
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
          >
            {({ isActive }) => (
              <>
                <Icon
                  size={18}
                  style={{
                    color: isActive
                      ? textColorOnIcon(couleurIcones)
                      : couleurIcones,
                  }}
                />
                <span>{t(key)}</span>
                {to === '/stock' && alertesStock > 0 && (
                  <span className="ml-auto bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {alertesStock}
                  </span>
                )}
              </>
            )}
          </NavLink>
        ))}

        {/* Bouton Aide — visible par tous les rôles */}
        <button
          onClick={() => { setOpen(false); setShowAide(true); }}
          style={{ color: couleurTexte }}
          className="w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all duration-200 text-sm hover:bg-white/10 mt-1"
        >
          <HelpCircle size={18} style={{ color: couleurIcones }} />
          <span>Aide</span>
        </button>
      </nav>

      {/* Bas de sidebar */}
      <div
        className="p-3 border-t space-y-2"
        style={{ borderColor: couleurTexte === '#ffffff'
          ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)' }}
      >
        {/* Sélecteur langue */}
        <div className="relative">
          <button
            onClick={() => setLangOpen(!langOpen)}
            style={{ color: couleurTexte }}
            className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-white/10 text-sm"
          >
            <span>{currentLangLabel?.flag} {currentLangLabel?.label}</span>
            <ChevronDown size={14} style={{ color: couleurIcones }} />
          </button>
          {langOpen && (
            <div
              className="absolute bottom-full left-0 right-0 mb-1 rounded-lg overflow-hidden shadow-xl"
              style={{ backgroundColor: couleurFond, border: `1px solid ${couleurTexte}30` }}
            >
              {availableLanguages.map(l => (
                <button
                  key={l.code}
                  onClick={() => handleLang(l.code)}
                  style={{ color: l.code === lang ? couleurIcones : couleurTexte }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-white/10 transition-colors"
                >
                  {l.flag} {l.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info utilisateur — clic = Mon Profil */}
        <button
          onClick={() => { setOpen(false); navigate('/mon-profil'); }}
          title="Voir mon profil"
          className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg hover:opacity-90 transition-opacity text-left"
          style={{ backgroundColor: couleurTexte === '#ffffff'
            ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)' }}
        >
          {(() => {
            const photo = resolveUploadUrl(user?.photo_url);
            return photo ? (
              <img
                src={photo}
                alt=""
                className="w-8 h-8 rounded-full object-cover shrink-0"
                onError={e => { e.target.style.display = 'none'; }}
              />
            ) : (
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold shrink-0"
                style={{ backgroundColor: couleurIcones, color: textColorOnIcon(couleurIcones) }}
              >
                {user ? initiales(user.prenom, user.nom) : <User size={14} />}
              </div>
            );
          })()}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate" style={{ color: couleurTexte }}>
              {user?.prenom} {user?.nom}
            </p>
            <p className="text-xs capitalize" style={{ color: couleurTexteSoft }}>
              {t(`roles.${user?.role}`)}
            </p>
          </div>
        </button>

        {/* Déconnexion */}
        <button
          onClick={handleLogout}
          className="w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-red-400 hover:bg-red-900/30 transition-colors text-sm"
        >
          <LogOut size={18} />
          <span>{t('nav.deconnexion')}</span>
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-64 h-screen fixed left-0 top-0 z-30 shadow-2xl">
        {SidebarContent()}
      </aside>

      {/* Mobile: bouton hamburger (caché si la bottom-nav fournit déjà l'ouverture) */}
      {!hideMobileToggle && (
        <div className="lg:hidden fixed top-4 left-4 z-50">
          <button
            onClick={() => setOpen(true)}
            aria-label="Ouvrir le menu"
            className="p-2 bg-gray-900 text-yellow-400 rounded-lg shadow-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <Menu size={22} />
          </button>
        </div>
      )}

      {/* Mobile: overlay + drawer (slide-in animé) */}
      <div
        className={`lg:hidden fixed inset-0 z-50 flex transition-opacity duration-200
          ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        aria-hidden={!open}
      >
        <div
          className="absolute inset-0 bg-black/60"
          onClick={() => setOpen(false)}
        />
        <div
          className={`relative w-72 max-w-[80vw] h-full shadow-2xl transform transition-transform duration-200 ease-out
            ${open ? 'translate-x-0' : '-translate-x-full'}`}
          role="dialog"
          aria-modal="true"
          aria-label="Menu principal"
        >
          <button
            onClick={() => setOpen(false)}
            aria-label="Fermer le menu"
            className="absolute top-4 right-4 z-10 text-gray-400 hover:text-white min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X size={22} />
          </button>
          {SidebarContent()}
        </div>
      </div>

      {/* Modal Guide d'utilisation */}
      {showAide && <AideModal onClose={() => setShowAide(false)} />}
    </>
  );
}
